import R from "@/modules/billing/referral/ReferralProgram.module.css";

import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { CreateBillingAccount } from "@/components/forms/CreateBillingAccount.tsx";
import { CurrencyPromo } from "@/components/l10n/Currency.tsx";
import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { useConfig } from "@/config.ts";
import { eBillingAccountQueryAtom } from "@/modules/billing/billingAccountQuery.ts";
import { getDefaultAccount, type EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { BillingSelectCustom } from "@/modules/billing/forms/BillingSelect.tsx";
import { getReferralUrl } from "@/modules/billing/referral/referralLogic.ts";
import C from "@/modules/dashboard/Dashboard.module.css";
import { cn } from "@/utils/classNames.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { Suspense, useState, type PropsWithChildren, type ReactNode } from "react";
import {
    FacebookIcon,
    FacebookShareButton,
    LinkedinIcon,
    LinkedinShareButton,
    TwitterShareButton,
    XIcon,
} from "react-share";

interface ReferralLayoutProps {
    variant: "billing_account" | "dashboard";
}

export interface ReferralProgramProps extends Omit<ReferralBoxProps, "account"> {
    items: EBillingAccount[];
}

function ReferralProgramCustom({ items, ...boxProps }: ReferralProgramProps) {
    //#region Hooks
    const actualDefault = getDefaultAccount(items);
    const [account, setAccount] = useState(actualDefault);
    //#endregion

    const select = (
        <div className={R.First}>
            <div className="color-muted font-size-small pb-1">
                {items.length > 1 ? <Trans>Choose billing account</Trans> : <Trans>Billing account</Trans>}
            </div>

            {items.length > 1 ? (
                <BillingSelectCustom
                    allowClosedAccounts
                    isItemDisabled={(item) => !item.canShareReferral}
                    onChange={setAccount}
                    items={items}
                    compact
                    isReferral
                />
            ) : (
                <div className={R.dummyField}>
                    <div className={cn(R.Label, "text-ellipsis")}>{account?.title ?? ""}</div>
                </div>
            )}
        </div>
    );

    return (
        <div className={C.Referral}>
            <h2 className="pb-3 pl-4">
                <Trans>Referral program</Trans>
            </h2>

            {account ? (
                <ReferralBox {...boxProps} account={account} select={select} />
            ) : (
                <ReferralEmptyBox {...boxProps} />
            )}
        </div>
    );
}

const introText = <Trans>You can earn and give free credits by sharing your unique referral code.</Trans>;

function ReferralLayout({ children, variant }: PropsWithChildren<ReferralLayoutProps>) {
    return (
        <div className={cn(R.Block, variant === "dashboard" ? R.Background : R.Custom)}>
            {variant === "dashboard" && <p className="font-size-title pb-3">{introText}</p>}
            {children}
        </div>
    );
}

function ReferralEmptyBox({ variant }: ReferralLayoutProps) {
    return (
        <ReferralLayout variant={variant}>
            <div className={R.Share}>
                <NoticeBlock color="primary" icon="jp-warning-icon" button={<CreateBillingAccount />}>
                    <Trans>An active billing account is needed to use this feature.</Trans>
                </NoticeBlock>
            </div>
        </ReferralLayout>
    );
}

export interface ReferralBoxProps extends ReferralLayoutProps {
    account: EBillingAccount;

    variant: "billing_account" | "dashboard";
    select?: ReactNode;
}

export function ReferralBox({ account, select, variant }: ReferralBoxProps) {
    //#region Hooks
    const { t } = useLingui();
    const { firstAccountCredit, referralCreditAmounts, siteName } = useConfig();
    //#endregion

    const { referralShareCode } = account;
    if (!referralShareCode) {
        console.warn("Referral share code is missing");
        return null;
    }

    const referralUrl = getReferralUrl(referralShareCode);

    //#region Amounts
    const promoterCredit = referralCreditAmounts.promoter_credit;
    // TODO: This can be not set, should we display a different message then?
    const minimumTopUp = referralCreditAmounts.receiver_precondition_topup_sum ?? 0;
    const totalReceiverCredit = referralCreditAmounts.receiver_credit
        ? referralCreditAmounts.receiver_credit + firstAccountCredit
        : 0;

    const c_promoterCredit = <CurrencyPromo value={promoterCredit} />;
    const c_minimumTopUp = <CurrencyPromo value={minimumTopUp} />;
    const c_totalReceiverCredit = <CurrencyPromo value={totalReceiverCredit} />;
    //#endregion

    return (
        <ReferralLayout variant={variant}>
            <ul>
                {variant === "billing_account" && <li>{introText}</li>}
                {account.isPostPay ? (
                    // TODO: Unify the text wording here to be more similar between the pre-pay and post-pay cases
                    <>
                        <li>
                            <Trans>
                                For every new user who signs up using your referral code and adds a verified payment
                                method, you will receive {c_promoterCredit} worth of free credits.
                            </Trans>
                        </li>
                        <li>
                            <Trans>
                                Every new user receives a {c_totalReceiverCredit} worth of free credits when signing up
                                through the referral link or code and adding a verified payment method.
                            </Trans>
                        </li>
                    </>
                ) : (
                    <>
                        <li>
                            <Trans>
                                For every new user who signs up through the referral program and tops up at least{" "}
                                {c_minimumTopUp}, you will receive {c_promoterCredit} worth of free credits.
                            </Trans>
                        </li>
                        <li>
                            <Trans>
                                Every new user who signs up through the referral program and tops up at least{" "}
                                {c_minimumTopUp} will receive a bonus worth of {c_totalReceiverCredit} credits.
                            </Trans>
                        </li>
                    </>
                )}
            </ul>

            <div className={R.Share}>
                {select}

                <div>
                    <div className="color-muted font-size-small pb-1">
                        <Trans>Referral code</Trans>
                    </div>
                    <div className={R.dummyField}>
                        <ClipBoardTooltip>{referralShareCode}</ClipBoardTooltip>
                    </div>
                </div>

                <div className={R.Last}>
                    <div className="color-muted font-size-small pb-1">
                        <Trans>Referral link</Trans>
                    </div>
                    <div className={R.dummyField}>
                        <ClipBoardTooltip>{referralUrl}</ClipBoardTooltip>
                    </div>
                </div>

                <div>
                    <div className="color-muted font-size-small pb-1">
                        <Trans>Social share</Trans>
                    </div>

                    <div className="flex gap-0.5em">
                        <FacebookShareButton
                            htmlTitle={t`Share on Facebook`}
                            className="share-button"
                            url={referralUrl}
                        >
                            <FacebookIcon size={36} borderRadius={4} />
                        </FacebookShareButton>
                        <TwitterShareButton
                            htmlTitle={t({
                                comment: "X is the social network",
                                message: "Share on X",
                            })}
                            className="share-button"
                            title={siteName}
                            url={referralUrl}
                        >
                            <XIcon size={36} borderRadius={4} />
                        </TwitterShareButton>
                        <LinkedinShareButton
                            htmlTitle={t`Share on LinkedIn`}
                            className="share-button"
                            title={siteName}
                            url={referralUrl}
                        >
                            <LinkedinIcon size={36} borderRadius={4} />
                        </LinkedinShareButton>
                    </div>
                </div>
            </div>
        </ReferralLayout>
    );
}

export interface ReferralProgramPropsLoaderProps extends Omit<ReferralProgramProps, "items"> {}

function ReferralProgramLoader(props: ReferralProgramPropsLoaderProps) {
    const data = useSuspenseQueryAtom(eBillingAccountQueryAtom);
    return <ReferralProgramCustom items={[...data.values()]} {...props} />;
}

export function ReferralProgram(props: ReferralProgramPropsLoaderProps) {
    // NB: No fallback to prevent flickering between "no accounts" and "has accounts" designs
    return (
        <Suspense fallback={null}>
            <ReferralProgramLoader {...props} />
        </Suspense>
    );
}
